import { debounce, DebouncedFunc } from 'lodash'
import { configureStore, UnknownAction } from '@reduxjs/toolkit'
import { createEpicMiddleware } from 'redux-observable'
import { Connection } from 'messaging'
import { Configuration } from 'common'
import { globalReducer, GlobalState } from 'store'
import { EntityType, EntityUpdateAction, HeartbeatAction, ServerActionTypes } from 'api'
import { isOpenFin } from 'utils'
import { globalEpic, Dependencies } from 'epics'
import {
  browserAudioService,
  browserWindowService,
  createBrowserNotificationService,
  openFinAudioService,
  openFinNotificationService,
  openFinWindowService,
  routerService
} from 'services'
import { TimeActions, TimeActionTypes } from './store/time'
import { diagnosticsLogger } from 'logger/diagnosticsLogger'

export function createStore(
  configuration: Configuration,
  testConfig: { connection?: Connection<any, any>; initialState?: Partial<GlobalState> } = {}
) {
  const dependencies: Dependencies = {
    connection: testConfig.connection ?? new Connection(),
    routerService,
    notificationService: isOpenFin() ? openFinNotificationService : createBrowserNotificationService(),
    windowService: isOpenFin() ? openFinWindowService : browserWindowService,
    audioService: isOpenFin() ? openFinAudioService : browserAudioService,
    diagnosticsLogger
  }

  const initialState = testConfig.initialState ?? { configurationStore: configuration }
  const epicMiddleware = createEpicMiddleware<UnknownAction, UnknownAction, GlobalState, Dependencies>({ dependencies })

  const store = configureStore({
    reducer: globalReducer,
    devTools: true,
    preloadedState: initialState,
    middleware: getDefaultMiddleware => {
      const middlewares = getDefaultMiddleware({
        thunk: { extraArgument: dependencies },
        serializableCheck: false,
        immutableCheck: false,
        actionCreatorCheck: false
      }).prepend(epicMiddleware)

      if (process.env.NODE_ENV === 'development') middlewares.push(getLogger())

      return middlewares
    },

    enhancers: getStoreEnhancer => getStoreEnhancer({ autoBatch: { type: 'callback', queueNotification: createQueueNotification() } })
  })

  epicMiddleware.run(globalEpic)

  return store
}

function createQueueNotification() {
  let debouncedNotify: (Function & DebouncedFunc<any>) | null = null
  const SUBSCRIBE_DEBOUNCE_PERIOD = 100
  const SUBSCRIBE_MAX_WAIT = 2000

  return function queueNotification(notify: () => void) {
    if (debouncedNotify === null) {
      debouncedNotify = debounce(notify, SUBSCRIBE_DEBOUNCE_PERIOD, { maxWait: SUBSCRIBE_MAX_WAIT })
    }

    debouncedNotify()
  }
}

function getLogger() {
  const { createLogger } = require('redux-logger')

  return createLogger({
    // don't log the snapshot update messages
    predicate: (_getState: () => GlobalState, action: EntityUpdateAction | HeartbeatAction | TimeActions) => {
      return !(
        (action.type === ServerActionTypes.ENTITY_UPDATE && action.payload.isSnapshot) ||
        (action.type === ServerActionTypes.ENTITY_UPDATE && action.payload.update.entityType === EntityType.COLLATERAL_PRICE) ||
        action.type === ServerActionTypes.HEARTBEAT ||
        action.type === TimeActionTypes.SET_TIMES
      )
    }
  })
}
